import { useState, useEffect, useRef, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import {
  fetchInboxMessages,
  fetchConversationThread,
  replyToMessage,
  markAsRead,
  deleteMessage,
  classifyMessage,
} from "../../services/graphInbox";
import { supabase, dbToDeal, dealToDb, uploadDocument, extractTextFromFile, pdfToImages } from "../../supabase";
import { STAGES } from "../../constants/dealStages";
import { callClaudeWithMessages } from "../../services/claude";
import { geocodeAddress } from "../../services/geocoding";
import { logActivity, autoIncrementTasks } from "../../services/activityLogger";
import iconWhite from "../../assets/logos/icon-white.png";

// ═══════════════════════════════════════════════════════════════════════════════
// INBOX VIEW — acquisitions@proximityparks.com dashboard
// ═══════════════════════════════════════════════════════════════════════════════
// Two streams:
//   1. Outbound Replies (PP red) — responses to LOIs we sent
//   2. Inbound (blue)            — broker blasts, new deal inquiries
//
// Auto-polls every 30s + manual refresh button.

const POLL_INTERVAL = 30000;

// Inline style helpers to avoid theme-light CSS overrides on white text
const whiteText = { color: "#ffffff" };

export default function InboxView({ onNavigateToDeal }) {
  const { instance: msalInstance, accounts } = useMsal();
  const currentAccount = accounts?.[0];
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [outreachEmails, setOutreachEmails] = useState([]);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [filter, setFilter] = useState("all"); // all | outbound | inbound
  const [searchTerm, setSearchTerm] = useState("");
  // "Add Deal Items" panel state
  const [addDealOpen, setAddDealOpen] = useState(false);
  const [addDealStage, setAddDealStage] = useState("Screening");
  const [addDealMarketed, setAddDealMarketed] = useState(false);
  const [addDealFiles, setAddDealFiles] = useState([]); // File[]
  const [addDealProcessing, setAddDealProcessing] = useState(false);
  const [addDealStatus, setAddDealStatus] = useState(""); // progress text
  const addDealFileRef = useRef(null);
  // "Summarize & Add Email to Deal" state
  const [summarizing, setSummarizing] = useState(false);
  const [summarizeStatus, setSummarizeStatus] = useState("");
  const pollRef = useRef(null);
  const threadEndRef = useRef(null);

  // ── Load outreach contact emails so we can classify outbound replies ────
  useEffect(() => {
    async function loadOutreachContacts() {
      const PAGE = 1000;
      let from = 0;
      let emails = [];
      while (true) {
        const { data } = await supabase
          .from("outreach_log")
          .select("contact_id")
          .range(from, from + PAGE - 1);
        if (!data || data.length === 0) break;
        const contactIds = [...new Set(data.map(o => o.contact_id).filter(Boolean))];
        if (contactIds.length > 0) {
          const { data: contacts } = await supabase
            .from("contacts")
            .select("email1, email2, email3")
            .in("id", contactIds);
          if (contacts) {
            contacts.forEach(c => {
              if (c.email1) emails.push(c.email1);
              if (c.email2) emails.push(c.email2);
              if (c.email3) emails.push(c.email3);
            });
          }
        }
        if (data.length < PAGE) break;
        from += PAGE;
      }
      setOutreachEmails([...new Set(emails)]);
    }
    loadOutreachContacts();
  }, []);

  // ── Fetch inbox ────────────────────────────────────────────────────────────
  const fetchMail = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const { messages: msgs } = await fetchInboxMessages(msalInstance, { top: 200 });
      // Classify each message
      const classified = msgs.map(m => ({
        ...m,
        _classification: classifyMessage(m, outreachEmails),
      }));
      setMessages(classified);
    } catch (e) {
      console.error("Inbox fetch error:", e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [msalInstance, outreachEmails]);

  // Initial load + auto-poll
  useEffect(() => {
    fetchMail(true);
    pollRef.current = setInterval(() => fetchMail(false), POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchMail]);

  // ── Open a conversation thread ─────────────────────────────────────────────
  async function handleDeleteEmail(msg, e) {
    if (e) e.stopPropagation();
    if (!window.confirm("Delete this email from the inbox?")) return;
    try {
      await deleteMessage(msalInstance, msg.id);
      setMessages(prev => prev.filter(m => m.id !== msg.id));
      if (selectedMsg?.id === msg.id) setSelectedMsg(null);
    } catch (err) {
      console.error("Delete email error:", err);
      alert("Failed to delete: " + err.message);
    }
  }

  async function openThread(msg) {
    setSelectedMsg(msg);
    setThread([]);
    setReplyText("");
    setThreadLoading(true);
    try {
      if (!msg.isRead) {
        markAsRead(msalInstance, msg.id).catch(() => {}); // fire and forget
      }
      const threadMsgs = await fetchConversationThread(msalInstance, msg.conversationId);
      setThread(threadMsgs.length > 0 ? threadMsgs : [msg]);
    } catch (e) {
      console.error("Thread fetch error:", e);
      setThread([msg]);
    } finally {
      setThreadLoading(false);
    }
  }

  // Scroll to bottom of thread when new messages load
  useEffect(() => {
    if (threadEndRef.current) threadEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  // ── Reply handler ──────────────────────────────────────────────────────────
  async function handleReply() {
    if (!replyText.trim() || !selectedMsg || replying) return;
    setReplying(true);
    try {
      // Reply to the last message in the thread
      const lastMsg = thread[thread.length - 1] || selectedMsg;
      const htmlComment = `<div style="font-family:'Montserrat',Helvetica,Arial,sans-serif;font-size:14px;color:#333;">${replyText.replace(/\n/g, "<br/>")}</div>`;
      await replyToMessage(msalInstance, lastMsg.id, htmlComment);
      setReplyText("");
      // Refresh thread
      const updated = await fetchConversationThread(msalInstance, selectedMsg.conversationId);
      setThread(updated.length > 0 ? updated : thread);
      // Also refresh main inbox
      fetchMail(false);
    } catch (e) {
      console.error("Reply error:", e);
      alert("Failed to send reply: " + e.message);
    } finally {
      setReplying(false);
    }
  }

  // ── Add Deal Items: ONE deal per email. All uploaded files become documents
  //    on that single deal. Deal info is extracted from the first parseable
  //    file. Photo from page 1 of the first PDF. Comps extracted from ALL files.
  async function handleAddDealItems() {
    if (addDealFiles.length === 0 || addDealProcessing) return;
    setAddDealProcessing(true);
    try {
      const files = addDealFiles;
      const emailFrom = selectedMsg?.from?.emailAddress?.name || selectedMsg?.from?.emailAddress?.address || "";
      const emailSubject = selectedMsg?.subject || "";

      // ── Step 1: Extract text from all files, find deal info from the first parseable one ──
      let dealFields = null;
      const fileTexts = []; // { file, text }
      for (const file of files) {
        setAddDealStatus(`Reading ${file.name}...`);
        let text = "";
        try { text = await extractTextFromFile(file); } catch { continue; }
        if (text && text.trim().length >= 50) fileTexts.push({ file, text });
      }

      if (fileTexts.length > 0) {
        setAddDealStatus(`AI analyzing deal info...`);
        const prompt = `You are a commercial real estate deal analyst. Extract deal information from this document (an OM, broker blast, or rent roll).

Return ONLY valid JSON:
{
  "name": "deal/property name",
  "addresses": ["street address 1", "street address 2"],
  "city": "city",
  "state": "state",
  "market": "submarket or metro area",
  "squareFeet": numeric or null,
  "askingPrice": numeric (no $ or commas) or null,
  "capRate": numeric (whole percent, e.g. 5.5) or null,
  "sourceNotes": "brief summary of the opportunity (2-3 sentences max)"
}

Rules:
- Extract as much as you can find; use null for missing fields.
- askingPrice: "$30,000,000" becomes 30000000.
- squareFeet: "100,510 SF" becomes 100510.
- Return only JSON, no prose.`;

        try {
          const result = await callClaudeWithMessages(prompt, [
            { role: "user", content: fileTexts[0].text.slice(0, 50000) },
          ], 2048);
          const cleaned = result.replace(/```json?\s*/gi, "").replace(/```/g, "").trim();
          const fb = cleaned.indexOf("{"), lb = cleaned.lastIndexOf("}");
          dealFields = JSON.parse(fb >= 0 && lb > fb ? cleaned.slice(fb, lb + 1) : cleaned);
        } catch {
          // Couldn't parse — we'll still create a deal with email metadata
        }
      }

      // ── Step 2: Build deal row ──
      let addresses = dealFields?.addresses || [];
      if (addresses.length === 0) {
        const fallback = [dealFields?.city, dealFields?.state].filter(Boolean).join(", ");
        if (fallback) addresses = [fallback];
      }
      addresses = addresses.map(a => {
        if (dealFields?.city && !a.toLowerCase().includes(dealFields.city.toLowerCase())) {
          return [a, dealFields.city, dealFields.state].filter(Boolean).join(", ");
        }
        return a;
      });

      const dealName = dealFields?.name || emailSubject.replace(/^(re|fw|fwd):\s*/i, "").trim() || "Untitled Deal";
      const sourceNotes = [
        `Name: ${dealName}`,
        dealFields?.sourceNotes || "",
        `From inbox: "${emailSubject}" by ${emailFrom}`,
        files.length > 0 ? `Files: ${files.map(f => f.name).join(", ")}` : "",
      ].filter(Boolean).join("\n\n");

      setAddDealStatus(`Creating deal: ${dealName}...`);
      const dealRow = {
        address: JSON.stringify(addresses),
        market: dealFields?.market || "",
        square_footage: dealFields?.squareFeet || 0,
        asking_price: dealFields?.askingPrice || 0,
        cap_rate: dealFields?.capRate || 0,
        deal_stage: addDealStage,
        marketed: addDealMarketed,
        source_notes: sourceNotes,
        photo_path: "",
        thesis: "",
        open_questions: "",
        red_flags: "",
      };

      const { data: inserted, error: insertErr } = await supabase
        .from("deals").insert([dealRow]).select().single();

      if (insertErr) {
        setAddDealStatus(`Failed to create deal: ${insertErr.message}`);
        setAddDealProcessing(false);
        return;
      }

      // Log the new deal so the weekly stage-entry counters on the home
      // dashboard reflect deals added from the inbox flow.
      await logActivity({
        userName: currentAccount?.name || "Inbox Import",
        userEmail: currentAccount?.username || "",
        action: "deal_created",
        description: `${dealName} added to Pipeline (${addDealStage}) from Inbox`,
        entityType: "deal",
        entityId: inserted.id,
        metadata: { stage: addDealStage, deal_name: dealName, source: "inbox_import" },
      });
      await autoIncrementTasks(currentAccount?.username, "deal_created");

      // ── Step 3: Extract photo from page 1 of first PDF ──
      const firstPdf = files.find(f => f.name.toLowerCase().endsWith(".pdf"));
      if (firstPdf) {
        try {
          setAddDealStatus(`Extracting cover photo...`);
          const { images } = await pdfToImages(firstPdf, 1);
          if (images.length > 0) {
            const byteString = atob(images[0]);
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let j = 0; j < byteString.length; j++) ia[j] = byteString.charCodeAt(j);
            const photoBlob = new Blob([ab], { type: "image/jpeg" });
            const storagePath = `${inserted.id}/photo_${Date.now()}_cover.jpg`;
            const { error: photoErr } = await supabase.storage
              .from("deal-documents").upload(storagePath, photoBlob, { contentType: "image/jpeg" });
            if (!photoErr) await supabase.from("deals").update({ photo_path: storagePath }).eq("id", inserted.id);
          }
        } catch (e) { console.warn("Photo extraction failed:", e.message); }
      }

      // ── Step 4: Upload ALL files as documents on this deal ──
      for (const file of files) {
        setAddDealStatus(`Uploading ${file.name}...`);
        try { await uploadDocument(inserted.id, file); } catch (e) { console.error("Doc upload:", e); }
      }

      // ── Step 5: Geocode in background ──
      if (addresses[0]) {
        geocodeAddress(addresses[0]).then(coords => {
          if (coords) supabase.from("deals").update({ latitude: coords.lat, longitude: coords.lng }).eq("id", inserted.id);
        });
      }

      // ── Step 6: Extract comps from comp-bearing files only ──
      // Skip files that are clearly not OMs / rent rolls (income statements,
      // T-12s, financials, surveys, photos, brochures, NDAs, etc.) so we
      // don't burn Claude calls on docs that have no comps to extract.
      const COMP_SKIP_RE = /\b(income statement|t-?12|trailing 12|p ?and ?l|p&l|profit ?and ?loss|operating statement|budget|financials?|cash flow|balance sheet|taxe?s?|tax return|nda|confidentiality|survey|alta|title|insurance|environmental|phase [i1]|esa|appraisal|inspection|brochure|flyer|teaser|map|aerial|photo|picture|image|render|elevation|site plan|floor plan|zoning|permit)\b/i;
      const normalizeName = (n) => String(n || '').toLowerCase().replace(/\.[a-z0-9]+$/, '').replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ').trim();
      let totalComps = 0;
      for (const { file, text } of fileTexts) {
        if (COMP_SKIP_RE.test(normalizeName(file.name))) {
          setAddDealStatus(`Skipping ${file.name} (not an OM/rent roll)...`);
          continue;
        }
        setAddDealStatus(`Extracting comps from ${file.name}...`);
        try {
          const compPrompt = `You are a commercial real estate data extraction specialist. Extract ALL lease comps and sale comps from this document.

THERE ARE THREE SOURCES THAT EACH BECOME LEASE COMPS:
1. RENT ROLL — each tenant row is a lease comp. Use the SUBJECT PROPERTY address for all rows. Skip vacant rows. Extract ALL signed leases regardless of date.
2. LEASE COMPS section — comparable leases at other buildings. Each has its own address.
3. SALE COMPS section — comparable sales at other buildings.

Return ONLY valid JSON:
{
  "lease_comps": [
    { "source": "rent_roll" or "lease_comp", "property_name": "...", "address": "...", "city": "...", "state": "...", "market": "...", "tenant": "...", "tenant_industry": "...", "building_sf": null or number, "suite_sf": null or number, "lease_rate": null or number (annual $/SF), "lease_type": "NNN" or "Gross" or "Modified Gross", "opex_psf": null or number (annual opex per SF - taxes + insurance + CAM; only if stated, never estimated), "comp_date": "YYYY-MM-DD" or null, "landlord": "...", "loading_type": null, "door_type": null, "notes": "..." }
  ],
  "sale_comps": [
    { "property_name": "...", "address": "...", "city": "...", "state": "...", "market": "...", "buyer": "...", "seller": "...", "sale_price": null or number, "price_psf": null or number, "building_sf": null or number, "cap_rate": null or number (percentage), "comp_date": "YYYY-MM-DD" or null, "portfolio_name": null, "notes": "..." }
  ]
}

Rules:
- For rent_roll rows: use subject property address from OM cover for every row.
- Lease rates = annual $/SF. Cap rates as percentages. Dates in YYYY-MM-DD.
- Extract ALL rent-roll rows regardless of Lease Start date. Every signed lease is a comp.
- Return empty arrays if none found. No text outside the JSON.`;

          const compResult = await callClaudeWithMessages(compPrompt, [
            { role: "user", content: `Extract all comps:\n\n${text.slice(0, 200000)}` },
          ], 16384);

          let compParsed;
          let cc = compResult.replace(/```json?\s*/gi, "").replace(/```/g, "").trim();
          const fb2 = cc.indexOf("{"), lb2 = cc.lastIndexOf("}");
          if (fb2 >= 0 && lb2 > fb2) cc = cc.slice(fb2, lb2 + 1);
          try { compParsed = JSON.parse(cc); } catch {
            const lei = cc.lastIndexOf("},");
            if (lei > 0) {
              let sal = cc.slice(0, lei + 1);
              let oS = 0, oC = 0, inS = false, esc = false;
              for (const ch of sal) { if (esc) { esc = false; continue; } if (ch === "\\") { esc = true; continue; } if (ch === '"') { inS = !inS; continue; } if (inS) continue; if (ch === "[") oS++; else if (ch === "]") oS--; else if (ch === "{") oC++; else if (ch === "}") oC--; }
              sal += "]".repeat(Math.max(0, oS)) + "}".repeat(Math.max(0, oC));
              try { compParsed = JSON.parse(sal); } catch { compParsed = null; }
            }
          }

          if (compParsed) {
            const leaseComps = compParsed.lease_comps || [];
            const saleComps = compParsed.sale_comps || [];
            const isActive = (type, date) => {
              if (!date) return true;
              return type === "lease"
                ? new Date(date) > new Date(Date.now() - 365 * 86400000)
                : new Date(date) > new Date(Date.now() - 2 * 365 * 86400000);
            };

            for (const lc of leaseComps) {
              if (!lc.address) continue;
              const coords = await geocodeAddress([lc.address, lc.city, lc.state].filter(Boolean).join(", "));
              await supabase.from("comps").insert([{
                type: "lease", status: isActive("lease", lc.comp_date) ? "active" : "expired",
                property_name: lc.property_name || null, address: lc.address, city: lc.city || null, state: lc.state || null, market: lc.market || null,
                tenant: lc.tenant || null, tenant_industry: lc.tenant_industry || null,
                building_sf: lc.building_sf || null, suite_sf: lc.suite_sf || null,
                lease_rate: lc.lease_rate || null, lease_type: lc.lease_type || null,
                opex_psf: lc.opex_psf ?? null,
                comp_date: lc.comp_date || null, landlord: lc.landlord || null,
                loading_type: lc.loading_type || null, door_type: lc.door_type || null, notes: lc.notes || null,
                source: lc.source === "rent_roll" ? `Rent Roll: ${file.name}` : `PDF: ${file.name}`,
                added_by: "Inbox Import", latitude: coords?.lat || null, longitude: coords?.lng || null, from_deal_id: inserted.id,
              }]);
              totalComps++;
            }
            for (const sc of saleComps) {
              if (!sc.address) continue;
              const coords = await geocodeAddress([sc.address, sc.city, sc.state].filter(Boolean).join(", "));
              let psf = sc.price_psf || null;
              if (!psf && sc.sale_price && sc.building_sf) psf = Math.round(sc.sale_price / sc.building_sf);
              await supabase.from("comps").insert([{
                type: "sale", status: isActive("sale", sc.comp_date) ? "active" : "expired",
                property_name: sc.property_name || null, address: sc.address, city: sc.city || null, state: sc.state || null, market: sc.market || null,
                buyer: sc.buyer || null, seller: sc.seller || null, sale_price: sc.sale_price || null, price_psf: psf,
                building_sf: sc.building_sf || null, cap_rate: sc.cap_rate || null, comp_date: sc.comp_date || null, portfolio_name: sc.portfolio_name || null, notes: sc.notes || null,
                source: `PDF: ${file.name}`, added_by: "Inbox Import", latitude: coords?.lat || null, longitude: coords?.lng || null, from_deal_id: inserted.id,
              }]);
              totalComps++;
            }
          }
        } catch (compErr) {
          console.warn(`Comp extraction failed for ${file.name}:`, compErr.message);
        }
      }

      setAddDealStatus(`Done! Deal "${dealName}" created in ${addDealStage}. ${files.length} doc${files.length === 1 ? "" : "s"} uploaded${totalComps > 0 ? `, ${totalComps} comps extracted` : ""}.`);
      setAddDealFiles([]);
      setTimeout(() => { setAddDealStatus(""); setAddDealOpen(false); }, 5000);
    } catch (e) {
      setAddDealStatus(`Error: ${e.message}`);
    } finally {
      setAddDealProcessing(false);
    }
  }

  // ── Summarize email & add to matching deal's notes ────────────────────────
  async function handleSummarizeAndAddToDeal() {
    if (!selectedMsg || summarizing) return;
    setSummarizing(true);
    setSummarizeStatus("Reading email...");
    try {
      // 1. Get full thread text
      const threadText = thread.map(tm => {
        const from = tm.from?.emailAddress?.name || tm.from?.emailAddress?.address || "Unknown";
        const date = tm.receivedDateTime ? new Date(tm.receivedDateTime).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
        const div = document.createElement("div");
        div.innerHTML = tm.body?.content || tm.bodyPreview || "";
        const plainText = div.textContent || div.innerText || "";
        return `[${from} — ${date}]\n${plainText}`;
      }).join("\n\n---\n\n");

      const subject = selectedMsg.subject || "(no subject)";
      const fromName = selectedMsg.from?.emailAddress?.name || selectedMsg.from?.emailAddress?.address || "";

      // 2. Ask Claude to summarize AND identify the matching deal
      setSummarizeStatus("AI summarizing...");
      const prompt = `You are a CRE acquisitions analyst. You're reading an email thread from the team's acquisitions inbox.

Produce TWO things:

1. **deal_search**: A short string (property name, address, or deal name) that I can use to find the matching deal in our pipeline database. Extract this from the email subject line or body — look for property names, addresses, or deal names.

2. **summary**: A concise but thorough summary of the email thread. Include:
   - Who sent it and their role/brokerage
   - What property or deal it's about
   - Key deal terms mentioned (price, SF, cap rate, timeline)
   - Any action items or next steps
   - Any important context (CA required, tour dates, deadlines)

Return ONLY valid JSON:
{
  "deal_search": "property name or address to search for",
  "summary": "the summary paragraph"
}

No prose outside the JSON.`;

      const result = await callClaudeWithMessages(prompt, [
        { role: "user", content: `Email subject: ${subject}\nFrom: ${fromName}\n\nThread:\n${threadText.slice(0, 30000)}` },
      ], 2048);

      let parsed;
      const cleaned = result.replace(/```json?\s*/gi, "").replace(/```/g, "").trim();
      const fb = cleaned.indexOf("{"), lb = cleaned.lastIndexOf("}");
      parsed = JSON.parse(fb >= 0 && lb > fb ? cleaned.slice(fb, lb + 1) : cleaned);

      const { deal_search, summary } = parsed;
      if (!summary) throw new Error("AI returned no summary");

      // 3. Search for matching deal in pipeline
      setSummarizeStatus("Finding matching deal...");
      const PAGE = 1000;
      let allDeals = [];
      let from = 0;
      while (true) {
        const { data } = await supabase.from("deals").select("id, address, source_notes, market").range(from, from + PAGE - 1);
        if (!data || data.length === 0) break;
        allDeals = allDeals.concat(data);
        if (data.length < PAGE) break;
        from += PAGE;
      }

      // Fuzzy match: search deal_search terms against addresses + source_notes
      const searchLower = (deal_search || subject).toLowerCase();
      const searchTokens = searchLower.split(/[\s,]+/).filter(t => t.length > 2);

      let bestMatch = null;
      let bestScore = 0;
      for (const deal of allDeals) {
        let haystack = "";
        try {
          const addrs = JSON.parse(deal.address || "[]");
          haystack = (Array.isArray(addrs) ? addrs.join(" ") : deal.address || "") + " " + (deal.source_notes || "") + " " + (deal.market || "");
        } catch {
          haystack = (deal.address || "") + " " + (deal.source_notes || "") + " " + (deal.market || "");
        }
        const hay = haystack.toLowerCase();
        let score = 0;
        for (const token of searchTokens) {
          if (hay.includes(token)) score++;
        }
        if (score > bestScore) { bestScore = score; bestMatch = deal; }
      }

      if (!bestMatch || bestScore < 2) {
        setSummarizeStatus(`Could not find a matching deal for "${deal_search}". Try Add Deal Items instead.`);
        setTimeout(() => setSummarizeStatus(""), 5000);
        return;
      }

      // 4. Append summary to the deal's source_notes
      setSummarizeStatus("Adding to deal notes...");
      const timestamp = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
      const noteBlock = `\n\n--- Email Summary (${timestamp}) ---\nFrom: ${fromName}\nSubject: ${subject}\n\n${summary}`;

      const existingNotes = bestMatch.source_notes || "";
      await supabase.from("deals").update({ source_notes: existingNotes + noteBlock }).eq("id", bestMatch.id);

      // Extract deal name for the toast
      const nameMatch = existingNotes.match(/^Name:\s*(.+)$/m);
      const dealLabel = nameMatch ? nameMatch[1] : (deal_search || "the deal");

      setSummarizeStatus(`Added to "${dealLabel}" notes.`);
      setTimeout(() => setSummarizeStatus(""), 4000);
    } catch (e) {
      console.error("Summarize error:", e);
      setSummarizeStatus(`Error: ${e.message}`);
      setTimeout(() => setSummarizeStatus(""), 5000);
    } finally {
      setSummarizing(false);
    }
  }

  // Reset add-deal state when the thread modal closes
  useEffect(() => {
    if (!selectedMsg) {
      setAddDealOpen(false);
      setAddDealFiles([]);
      setAddDealStage("Screening");
      setAddDealStatus("");
    }
  }, [selectedMsg]);

  // ── Filter + search ────────────────────────────────────────────────────────
  const filtered = messages.filter(m => {
    if (filter === "outbound" && m._classification.type !== "outbound_reply") return false;
    if (filter === "inbound" && m._classification.type !== "inbound") return false;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      const hay = [
        m.subject,
        m.from?.emailAddress?.name,
        m.from?.emailAddress?.address,
        m.bodyPreview,
        m._classification.brokerage,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const outboundCount = messages.filter(m => m._classification.type === "outbound_reply").length;
  const inboundCount = messages.filter(m => m._classification.type === "inbound").length;
  const unreadCount = messages.filter(m => !m.isRead).length;

  // ── Helpers ────────────────────────────────────────────────────────────────
  function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  function extractBodyText(htmlBody) {
    if (!htmlBody) return "";
    const div = document.createElement("div");
    div.innerHTML = htmlBody;
    return div.textContent || div.innerText || "";
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading && messages.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="flex flex-col items-center gap-3">
          <div className="flex gap-1.5">
            <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
          <p className="text-sm" style={{ color: "#666" }}>Loading acquisitions inbox...</p>
        </div>
      </div>
    );
  }

  if (error && messages.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12 text-center">
        <div className="bg-[rgba(233,53,41,0.07)] border border-[rgba(233,53,41,0.25)] rounded-xl p-8">
          <p className="text-lg font-bold mb-2" style={{ color: "#333" }}>Inbox Unavailable</p>
          <p className="text-sm mb-4" style={{ color: "#666" }}>{error}</p>
          <p className="text-xs" style={{ color: "#999" }}>
            If this is a permissions error, ask IT to grant Mail.Read.Shared + Mail.ReadWrite.Shared + Mail.Send.Shared on the acquisitions@ mailbox.
          </p>
          <button onClick={() => fetchMail(true)} className="mt-4 bg-pp-red hover:bg-pp-red-light text-sm font-semibold px-4 py-2 rounded-lg transition-colors" style={whiteText}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Thread detail view (modal) ─────────────────────────────────────────────
  const threadModal = selectedMsg && (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(36,36,36,0.4)] backdrop-blur-sm overflow-y-auto p-4 sm:p-8" onClick={() => setSelectedMsg(null)}>
      <div className="rounded-2xl shadow-2xl w-full max-w-4xl my-4 overflow-hidden" style={{ backgroundColor: "#fff", border: "1px solid #d8d8d8" }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid #d8d8d8" }}>
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-2 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: selectedMsg._classification?.type === "outbound_reply" ? "#e93529" : "#8a8a8a" }} />
            <div className="min-w-0">
              <h2 className="text-base font-bold font-montserrat truncate" style={{ color: "#333" }}>{selectedMsg.subject || "(no subject)"}</h2>
              <p className="text-xs mt-0.5" style={{ color: "#888" }}>
                {selectedMsg.from?.emailAddress?.name || selectedMsg.from?.emailAddress?.address}
                {selectedMsg._classification?.brokerage && <span> &middot; {selectedMsg._classification.brokerage}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => handleDeleteEmail(selectedMsg)} className="rounded-lg w-8 h-8 flex items-center justify-center transition-colors hover:bg-[rgba(233,53,41,0.07)]" style={{ color: "#e93529" }} title="Delete email">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
            </button>
          <button onClick={() => setSelectedMsg(null)} className="rounded-lg w-8 h-8 flex items-center justify-center transition-colors hover:bg-[#ebebeb]" style={{ color: "#666" }}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          </div>
        </div>

        {/* Thread messages */}
        <div style={{ backgroundColor: "#f4f4f4", maxHeight: "calc(100vh - 320px)", overflowY: "auto", padding: "20px 24px" }}>
          {threadLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {thread.map((tm, i) => {
                const isFromUs = (tm.from?.emailAddress?.address || "").toLowerCase() === "acquisitions@proximityparks.com";
                return (
                  <div key={tm.id || i} className="rounded-xl p-4" style={{
                    backgroundColor: isFromUs ? "#fdeceb" : "#ffffff",
                    border: `1px solid ${isFromUs ? "#f5b8b4" : "#e4e4e4"}`,
                  }}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        {isFromUs ? (
                          <div className="w-6 h-6 rounded flex items-center justify-center bg-pp-red flex-shrink-0" style={{ padding: 3 }}>
                            <img src={iconWhite} alt="PP" style={{ width: "100%", height: "100%", objectFit: "contain" }} draggable={false} />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold" style={{ backgroundColor: "#8a8a8a", color: "#fff" }}>
                            {(tm.from?.emailAddress?.name || "?")[0].toUpperCase()}
                          </div>
                        )}
                        <span className="text-sm font-semibold" style={{ color: "#333" }}>
                          {isFromUs ? "ProximityParks Acquisitions" : (tm.from?.emailAddress?.name || tm.from?.emailAddress?.address)}
                        </span>
                      </div>
                      <span className="text-xs flex-shrink-0" style={{ color: "#999" }}>{formatDate(tm.receivedDateTime)}</span>
                    </div>
                    <div className="text-sm leading-relaxed" style={{ color: "#444" }} dangerouslySetInnerHTML={{ __html: tm.body?.content || tm.bodyPreview || "" }} />
                  </div>
                );
              })}
              <div ref={threadEndRef} />
            </div>
          )}
        </div>

        {/* Summarize & Add Email to Deal — for any email type */}
        <div style={{ borderTop: "1px solid #d8d8d8", padding: "12px 24px", backgroundColor: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="flex items-center gap-3">
            {/* Mark sender's email as invalid in Contacts */}
            <button
              onClick={async () => {
                try {
                  // Step 1: Detect the ACTUAL invalid email address.
                  // For bounce-backs, the From is "Microsoft Outlook" — useless.
                  // The real address is in the body: "Your message to X@Y couldn't be delivered"
                  // The body is HTML so we need to strip tags and handle entities.
                  const allBodyHtml = thread.map(t => t.body?.content || "").join(" ") + (selectedMsg?.bodyPreview || "");
                  // Strip HTML tags to get plain text for regex matching
                  const div = document.createElement("div");
                  div.innerHTML = allBodyHtml;
                  const plainBody = div.textContent || div.innerText || "";

                  let targetEmail = null;

                  // Pattern 1: "Your message to X@Y couldn't be delivered"
                  const bounceMatch = plainBody.match(/Your message to\s+(\S+@\S+)\s+couldn/i);
                  if (bounceMatch) targetEmail = bounceMatch[1].replace(/[<>]/g, "").toLowerCase();

                  // Pattern 2: "X@Y" after "couldn't be delivered" or "delivery has failed"
                  if (!targetEmail) {
                    const failMatch = plainBody.match(/(?:couldn't be delivered|delivery.*failed|undeliverable).*?(\S+@\S+)/i);
                    if (failMatch) targetEmail = failMatch[1].replace(/[<>]/g, "").toLowerCase();
                  }

                  // Pattern 3: Extract from HTML href="mailto:X@Y" in the body
                  if (!targetEmail) {
                    const hrefMatch = allBodyHtml.match(/mailto:([^"&\s]+@[^"&\s]+)/i);
                    if (hrefMatch) {
                      const candidate = hrefMatch[1].toLowerCase();
                      // Skip internal addresses
                      if (!candidate.includes("proximityparks.com") && !candidate.includes("microsoft")) {
                        targetEmail = candidate;
                      }
                    }
                  }

                  // Fallback: use the sender's email (for non-bounce emails)
                  const senderAddr = selectedMsg?.from?.emailAddress?.address?.toLowerCase() || "";
                  if (!targetEmail && senderAddr && !senderAddr.includes("microsoftexchange") && !senderAddr.includes("microsoft.com")) {
                    targetEmail = senderAddr;
                  }

                  if (!targetEmail) {
                    setSummarizeStatus("Could not detect the invalid email address from this message.");
                    setTimeout(() => setSummarizeStatus(""), 4000);
                    return;
                  }

                  if (!window.confirm(`Mark ${targetEmail} as invalid across all contacts?`)) return;

                  // Step 2: Find and update matching contacts
                  const updates = [];
                  const { data: matches } = await supabase.from("contacts").select("id, email1, email2, email3").or(`email1.eq.${targetEmail},email2.eq.${targetEmail},email3.eq.${targetEmail}`);
                  for (const c of (matches || [])) {
                    const patch = {};
                    if (c.email1?.toLowerCase() === targetEmail) patch.email1_status = "invalid";
                    if (c.email2?.toLowerCase() === targetEmail) patch.email2_status = "invalid";
                    if (c.email3?.toLowerCase() === targetEmail) patch.email3_status = "invalid";
                    if (Object.keys(patch).length > 0) {
                      await supabase.from("contacts").update(patch).eq("id", c.id);
                      updates.push(c.id);
                    }
                  }
                  setSummarizeStatus(updates.length > 0 ? `Marked ${targetEmail} invalid on ${updates.length} contact${updates.length === 1 ? "" : "s"}.` : `No contacts found with ${targetEmail}.`);
                  setTimeout(() => setSummarizeStatus(""), 5000);
                } catch (e) {
                  console.error("Mark invalid error:", e);
                  setSummarizeStatus(`Error: ${e.message}`);
                  setTimeout(() => setSummarizeStatus(""), 5000);
                }
              }}
              className="flex items-center gap-2 bg-[rgba(233,53,41,0.07)] hover:bg-[rgba(233,53,41,0.14)] text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
              style={{ color: "#e93529" }}
              title="Mark the sender's email as invalid in Sourcing contacts"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
              Mark Email Invalid
            </button>
            <button
              onClick={handleSummarizeAndAddToDeal}
              disabled={summarizing || thread.length === 0}
              className="flex items-center gap-2 bg-[#ebebeb] hover:bg-[#d8d8d8] disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
              style={{ color: "#333" }}
              title="Summarize this email thread and append it to the matching deal's notes in the Pipeline"
            >
              {summarizing ? (
                <>
                  <span className="inline-block w-3 h-3 border-2 border-[#8a8a8a] border-t-transparent rounded-full animate-spin" />
                  Summarizing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  Summarize &amp; Add to Deal
                </>
              )}
            </button>
          </div>
          {summarizeStatus && (
            <span className="text-xs" style={{ color: summarizeStatus.startsWith("Added") || summarizeStatus.startsWith("Done") ? "#44906a" : summarizeStatus.startsWith("Error") || summarizeStatus.startsWith("Could not") ? "#e93529" : "#888" }}>
              {summarizeStatus}
            </span>
          )}
        </div>

        {/* Add Deal Items — only for inbound emails */}
        {selectedMsg._classification?.type === "inbound" && (
          <div style={{ borderTop: "1px solid #d8d8d8", backgroundColor: "#fff" }}>
            <button
              onClick={() => setAddDealOpen(v => !v)}
              className="w-full flex items-center justify-between px-6 py-3 text-left transition-colors hover:bg-[#f4f4f4]"
            >
              <div className="flex items-center gap-2">
                <svg className={`w-3.5 h-3.5 transition-transform ${addDealOpen ? "rotate-90" : ""}`} fill="none" stroke="#e93529" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                <span className="text-sm font-bold" style={{ color: "#e93529" }}>Add Deal Items</span>
              </div>
              <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#999" }}>
                Upload OM or Rent Roll to create a deal
              </span>
            </button>

            {addDealOpen && (
              <div className="px-6 pb-5">
                {/* Stage selector */}
                <div className="flex items-center gap-3 mb-3">
                  <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#666" }}>Stage:</label>
                  <select
                    value={addDealStage}
                    onChange={e => setAddDealStage(e.target.value)}
                    className="rounded-lg px-3 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-pp-red/40"
                    style={{ backgroundColor: "#f4f4f4", border: "1px solid #d8d8d8", color: "#333" }}
                  >
                    {STAGES.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-2 cursor-pointer ml-2">
                    <input
                      type="checkbox"
                      checked={addDealMarketed}
                      onChange={e => setAddDealMarketed(e.target.checked)}
                      className="accent-pp-red w-4 h-4"
                    />
                    <span className="text-xs font-semibold" style={{ color: addDealMarketed ? "#e93529" : "#666" }}>
                      {addDealMarketed ? "On-Market" : "Off-Market"}
                    </span>
                  </label>
                </div>

                {/* File drop zone */}
                <div
                  className="rounded-xl p-6 text-center cursor-pointer transition-colors hover:bg-[#ebebeb]"
                  style={{ border: "2px dashed #d8d8d8", backgroundColor: "#fafafa" }}
                  onClick={() => addDealFileRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#e93529"; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = "#d8d8d8"; }}
                  onDrop={e => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = "#d8d8d8";
                    const files = Array.from(e.dataTransfer.files).filter(f =>
                      /\.(pdf|docx?|xlsx?|csv)$/i.test(f.name)
                    );
                    if (files.length) setAddDealFiles(prev => [...prev, ...files]);
                  }}
                >
                  <input
                    ref={addDealFileRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xlsx,.xls,.csv"
                    className="hidden"
                    onChange={e => {
                      const files = Array.from(e.target.files);
                      if (files.length) setAddDealFiles(prev => [...prev, ...files]);
                      e.target.value = "";
                    }}
                  />
                  <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="#999" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                  <p className="text-sm font-medium" style={{ color: "#666" }}>
                    Drop OMs, Rent Rolls, or deal docs here
                  </p>
                  <p className="text-xs mt-1" style={{ color: "#999" }}>
                    PDF, Word, Excel — all files attach to one deal
                  </p>
                </div>

                {/* File list */}
                {addDealFiles.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {addDealFiles.map((f, i) => (
                      <div key={i} className="flex items-center justify-between rounded-lg px-3 py-2" style={{ backgroundColor: "#f4f4f4" }}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm flex-shrink-0">
                            {f.name.endsWith(".pdf") ? "📄" : f.name.match(/\.xlsx?$/i) ? "📊" : "📝"}
                          </span>
                          <span className="text-sm truncate" style={{ color: "#333" }}>{f.name}</span>
                          <span className="text-[10px] flex-shrink-0" style={{ color: "#999" }}>
                            {(f.size / 1024 / 1024).toFixed(1)} MB
                          </span>
                        </div>
                        <button
                          onClick={() => setAddDealFiles(prev => prev.filter((_, j) => j !== i))}
                          className="text-[#8a8a8a] hover:text-[#e93529] transition-colors p-1 flex-shrink-0"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Status + action */}
                <div className="flex items-center justify-between mt-4">
                  <span className="text-xs" style={{ color: addDealStatus.startsWith("Done") ? "#44906a" : addDealStatus.startsWith("Error") || addDealStatus.startsWith("Failed") || addDealStatus.startsWith("Skipped") ? "#e93529" : "#888" }}>
                    {addDealStatus || (addDealFiles.length > 0 ? `${addDealFiles.length} file${addDealFiles.length === 1 ? "" : "s"} ready → stage: ${addDealStage}` : "")}
                  </span>
                  <button
                    onClick={handleAddDealItems}
                    disabled={addDealFiles.length === 0 || addDealProcessing}
                    className="bg-pp-red hover:bg-pp-red-light disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold px-5 py-2 rounded-lg transition-colors flex items-center gap-2"
                    style={whiteText}
                  >
                    {addDealProcessing ? (
                      <>
                        <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="#fff" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        Create Deal
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reply box */}
        <div style={{ borderTop: "1px solid #d8d8d8", padding: "16px 24px", backgroundColor: "#fff" }}>
          <div className="flex gap-3">
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleReply(); }}
              placeholder="Type your reply... (Ctrl+Enter to send)"
              rows={3}
              className="flex-1 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-pp-red/40 font-montserrat"
              style={{ backgroundColor: "#f4f4f4", border: "1px solid #d8d8d8", color: "#333" }}
            />
            <button
              onClick={handleReply}
              disabled={!replyText.trim() || replying}
              className="self-end bg-pp-red hover:bg-pp-red-light disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold px-5 py-3 rounded-lg transition-colors flex items-center gap-2"
              style={whiteText}
            >
              {replying ? (
                <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="#fff" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
              )}
              {replying ? "Sending..." : "Reply"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Main inbox list ────────────────────────────────────────────────────────
  return (
    <div className="max-w-6xl mx-auto px-6 py-8 font-montserrat">
      {threadModal}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "#333" }}>Inbox</h1>
          <p className="text-sm mt-0.5" style={{ color: "#888" }}>
            acquisitions@proximityparks.com
            {unreadCount > 0 && <span className="ml-2 bg-pp-red text-xs font-bold px-2 py-0.5 rounded-full" style={whiteText}>{unreadCount} unread</span>}
          </p>
        </div>
        <button
          onClick={() => fetchMail(true)}
          disabled={loading}
          className="flex items-center gap-2 bg-[#ebebeb] hover:bg-[#d8d8d8] text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          style={{ color: "#333" }}
        >
          <svg className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          Refresh
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <button
          onClick={() => setFilter(filter === "outbound" ? "all" : "outbound")}
          className={`rounded-xl p-4 text-left transition-all border-2 ${filter === "outbound" ? "border-pp-red shadow-lg" : "border-transparent"}`}
          style={{ backgroundColor: "#fdeceb" }}
        >
          <p className="text-2xl font-bold" style={{ color: "#e93529" }}>{outboundCount}</p>
          <p className="text-xs font-semibold uppercase tracking-wider mt-1" style={{ color: "#e93529" }}>LOI Responses</p>
          <p className="text-[10px] mt-0.5" style={{ color: "#999" }}>Replies to offers you sent</p>
        </button>
        <button
          onClick={() => setFilter(filter === "inbound" ? "all" : "inbound")}
          className={`rounded-xl p-4 text-left transition-all border-2 ${filter === "inbound" ? "border-[#8a8a8a] shadow-lg" : "border-transparent"}`}
          style={{ backgroundColor: "#f4f4f4" }}
        >
          <p className="text-2xl font-bold" style={{ color: "#8a8a8a" }}>{inboundCount}</p>
          <p className="text-xs font-semibold uppercase tracking-wider mt-1" style={{ color: "#8a8a8a" }}>Inbound Deals</p>
          <p className="text-[10px] mt-0.5" style={{ color: "#999" }}>Broker blasts & new opportunities</p>
        </button>
        <div className="rounded-xl p-4" style={{ backgroundColor: "#f4f4f4" }}>
          <p className="text-2xl font-bold" style={{ color: "#333" }}>{messages.length}</p>
          <p className="text-xs font-semibold uppercase tracking-wider mt-1" style={{ color: "#888" }}>Total Messages</p>
          <p className="text-[10px] mt-0.5" style={{ color: "#999" }}>{unreadCount} unread</p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search sender, subject, brokerage..."
          className="w-full rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pp-red/40 font-montserrat"
          style={{ backgroundColor: "#f4f4f4", border: "1px solid #d8d8d8", color: "#333" }}
        />
      </div>

      {/* Filter label */}
      {filter !== "all" && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full" style={{
            backgroundColor: filter === "outbound" ? "#fdeceb" : "#f4f4f4",
            color: filter === "outbound" ? "#e93529" : "#8a8a8a",
          }}>
            {filter === "outbound" ? "Showing LOI Responses Only" : "Showing Inbound Only"}
          </span>
          <button onClick={() => setFilter("all")} className="text-xs underline" style={{ color: "#888" }}>Show all</button>
        </div>
      )}

      {/* Message list */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-lg mb-2" style={{ color: "#888" }}>
            {messages.length === 0 ? "No emails in the inbox yet." : "No emails match your filter."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(msg => {
            const cls = msg._classification;
            const isOutbound = cls.type === "outbound_reply";
            const accent = isOutbound ? "#e93529" : "#8a8a8a";
            const bgColor = isOutbound ? "#fdeceb" : "#ffffff";
            const borderColor = isOutbound ? "#f5b8b4" : "#e4e4e4";
            const fromName = msg.from?.emailAddress?.name || msg.from?.emailAddress?.address || "Unknown";
            const fromAddr = msg.from?.emailAddress?.address || "";

            return (
              <button
                key={msg.id}
                onClick={() => openThread(msg)}
                className="w-full text-left rounded-xl p-4 transition-all hover:shadow-md group"
                style={{
                  backgroundColor: bgColor,
                  borderLeft: `4px solid ${accent}`,
                  border: `1px solid ${borderColor}`,
                  borderLeftWidth: 4,
                  borderLeftColor: accent,
                  opacity: msg.isRead ? 0.85 : 1,
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {/* Type badge */}
                      {isOutbound ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: "#e93529", ...whiteText }}>
                          LOI Response
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: "#8a8a8a", ...whiteText }}>
                          Inbound
                        </span>
                      )}
                      {!msg.isRead && (
                        <span className="w-2 h-2 rounded-full bg-pp-red flex-shrink-0" title="Unread" />
                      )}
                      {cls.brokerage && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: "#f4f4f4", color: "#666" }}>
                          {cls.brokerage}
                        </span>
                      )}
                      {msg.importance === "high" && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: "#faf4e8", color: "#8a6a1f" }}>
                          High Priority
                        </span>
                      )}
                    </div>

                    {/* Subject */}
                    <p className={`text-sm truncate mb-0.5 ${msg.isRead ? "" : "font-bold"}`} style={{ color: "#333" }}>
                      {msg.subject || "(no subject)"}
                    </p>

                    {/* From */}
                    <p className="text-xs truncate" style={{ color: "#666" }}>
                      {fromName}
                      {fromAddr && fromName !== fromAddr && <span style={{ color: "#999" }}> &middot; {fromAddr}</span>}
                    </p>

                    {/* Preview */}
                    <p className="text-xs mt-1 line-clamp-2" style={{ color: "#888" }}>
                      {msg.bodyPreview}
                    </p>

                    {/* CA link badge */}
                    {(cls.caLinks.length > 0 || cls.hasCAMention) && (
                      <div className="flex items-center gap-2 mt-2">
                        {cls.caLinks.map((url, i) => (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="text-[10px] font-semibold px-2 py-1 rounded-lg transition-colors hover:bg-[rgba(233,53,41,0.12)] flex items-center gap-1"
                            style={{ backgroundColor: "#faf4e8", color: "#8a6a1f" }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            Sign CA {cls.caLinks.length > 1 ? `(${i + 1})` : ""}
                          </a>
                        ))}
                        {cls.hasCAMention && cls.caLinks.length === 0 && (
                          <span className="text-[10px] font-semibold px-2 py-1 rounded-lg" style={{ backgroundColor: "#faf4e8", color: "#8a6a1f" }}>
                            CA Mentioned
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Date + Delete */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs" style={{ color: "#999" }}>
                      {formatDate(msg.receivedDateTime)}
                    </span>
                    <button
                      onClick={(e) => handleDeleteEmail(msg, e)}
                      className="opacity-0 group-hover:opacity-100 text-[#8a8a8a] hover:text-[#e93529] transition-all p-1 rounded"
                      title="Delete email"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                    </button>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
